import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BriefcaseIcon,
  DocumentTextIcon,
  ChartBarIcon,
  SparklesIcon,
  RocketLaunchIcon,
  ClipboardDocumentCheckIcon,
  LightBulbIcon,
  CheckCircleIcon,
  BellAlertIcon,
  MapIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';

const HomePage: React.FC = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const gridBg = `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 0V100M21 0V100M41 0V100M61 0V100M81 0V100M0 1H100M0 21H100M0 41H100M0 61H100M0 81H100' stroke='white' stroke-opacity='0.1'/%3E%3C/svg%3E")`;

  const features = [
    {
      icon: <RocketLaunchIcon className="w-8 h-8" />,
      title: "A feed that opens on the right roles",
      description: "Every opening is scored against your titles, skills and location. The ones worth your time sit at the top. Everything else waits below."
    },
    {
      icon: <DocumentTextIcon className="w-8 h-8" />,
      title: "Know before you apply",
      description: "See how your CV reads against a specific role — which skills land, which gaps show, and what to fix before you hit send."
    },
    {
      icon: <ClipboardDocumentCheckIcon className="w-8 h-8" />,
      title: "One board, every application",
      description: "Saved, applied, interviewing, offer. Move a role across your board and always know exactly where you stand."
    },
    {
      icon: <MapIcon className="w-8 h-8" />,
      title: "Interview prep that stays with the job",
      description: "Every stage mapped out, with your notes and preparation attached to the role they belong to. Nothing scattered across notebooks."
    },
    {
      icon: <BellAlertIcon className="w-8 h-8" />,
      title: "New roles, scored on arrival",
      description: "Fresh openings arrive continuously and are ranked the moment they land, so the good ones surface while they are still open."
    },
    {
      icon: <LightBulbIcon className="w-8 h-8" />,
      title: "Sharper every time you use it",
      description: "Pass on a role and JOE takes note. Your feed tightens around what you actually want with every decision you make."
    }
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Tell JOE what you're after",
      description: "Five questions, two minutes. Roles, skills, location, seniority."
    },
    {
      step: "02",
      title: "Your feed rebuilds itself",
      description: "Every opening is ranked against your profile, best match first."
    },
    {
      step: "03",
      title: "Take it through to the offer",
      description: "Apply, track each stage, and prepare — all in one place."
    }
  ];

  const benefits = [
    "Ranking built around your priorities, not a job board's",
    "Every application in one place, from bookmark to signed offer",
    "Skill gaps surfaced before you apply, not after the rejection",
    "The noise filtered out, so you stop scrolling past roles you'd never take",
    "Interview prep attached to the role it belongs to",
    "A feed that gets more accurate the more you use it"
  ];

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  return (
    <>
      {/* <Header /> */}
      <div className="flex-1 overflow-hidden bg-gradient-to-b from-gray-50 to-white" style={{ willChange: 'auto' }}>
        {/* Hero Section - Modern Glassmorphism Design */}
        <div className="relative min-h-screen flex items-center">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 to-purple-700/90 overflow-hidden">
            {/* Modern Abstract Shapes */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
              <div className="absolute w-96 h-96 -top-12 -left-12 bg-blue-500/30 rounded-full blur-3xl" />
              <div className="absolute w-96 h-96 top-1/2 right-0 bg-purple-500/30 rounded-full blur-3xl" />
              <div className="absolute w-96 h-96 bottom-0 left-1/4 bg-pink-500/20 rounded-full blur-3xl" />
            </div>
          </div>

          <div className="relative container mx-auto px-4 py-20">
            <motion.div 
              className="max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="text-center mb-12">
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, ease: "easeOut" }}
                  className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-xl rounded-full px-4 py-2 mb-8"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-300 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-300"></span>
                  </span>
                  <span className="text-sm font-medium text-white">Live as of today</span>
                </motion.div>
                
                <h1 className="text-5xl md:text-6xl xl:text-7xl font-extrabold mb-6 text-white leading-tight">
                  The roles worth your time,{' '}
                  <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                    first
                  </span>
                </h1>

                <h2 className="text-xl md:text-2xl font-light mb-8 text-blue-100/90 leading-relaxed max-w-3xl mx-auto">
                  JOE learns what you're looking for, scores every opening against it, and tracks each application through to the offer. Built for tech and chemistry professionals.
                </h2>
                
                <motion.div 
                  className="flex flex-wrap justify-center gap-4 mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, ease: "easeOut" }}
                >
                  <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-sm font-medium text-white">Ranked for you</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span className="text-sm font-medium text-white">CV-aware matching</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span className="text-sm font-medium text-white">Tracked to the offer</span>
                  </div>
                </motion.div>
              </div>

              <motion.div 
                className="flex flex-col sm:flex-row gap-6 justify-center items-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, ease: "easeOut" }}
              >
                <Link
                  to="/register"
                  className="group relative overflow-hidden px-10 py-5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl font-bold text-white hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 flex items-center space-x-3 text-lg"
                >
                  <span>Build my feed — free</span>
                  <ArrowRightIcon className="w-6 h-6 transform group-hover:translate-x-1 transition-transform" />
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
                
                <button
                  onClick={() => setIsVideoOpen(true)}
                  className="group px-8 py-5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl font-medium text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300 flex items-center space-x-2"
                >
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mr-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span>Watch Demo</span>
                </button>
              </motion.div>

              {/* How it works - clarity in place of vanity metrics */}
              <motion.div
                className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 text-left"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, ease: "easeOut" }}
              >
                {howItWorks.map((item) => (
                  <div
                    key={item.step}
                    className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="text-sm font-mono font-bold text-cyan-300 mb-3">{item.step}</div>
                    <div className="text-lg font-semibold text-white mb-2">{item.title}</div>
                    <div className="text-blue-100/80 text-sm leading-relaxed">{item.description}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Features Grid - Modern Cards */}
        <div className="py-32 container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ease: "easeOut" }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <SparklesIcon className="w-4 h-4" />
              <span>Built for focused job seekers</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Built for the part nobody enjoys
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Searching, filtering, tracking, preparing. JOE handles the admin of a job hunt so your energy goes into the conversations that actually decide it.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                index={index}
              />
            ))}
          </div>
        </div>

        {/* Benefits Section - Modern List */}
        <div className="py-32 bg-gradient-to-b from-gray-50 to-blue-50/50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ease: "easeOut" }}
              className="max-w-4xl mx-auto"
            >
              <div className="text-center mb-16">
                <div className="inline-flex items-center space-x-2 bg-green-50 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Why JOE</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Made for people who take their career seriously
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  We went live today. Every early member helps shape what JOE becomes next.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, ease: "easeOut" }}
                    className="group flex items-start space-x-4 bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg border border-gray-100/50 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 p-2 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl group-hover:from-green-500 group-hover:to-emerald-500 transition-all duration-300">
                      <CheckCircleIcon className="w-5 h-5 text-green-600 group-hover:text-white" />
                    </div>
                    <span className="text-gray-800 font-medium leading-relaxed pt-1">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white py-32">
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.03\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"2\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')" }} />
          <div className="relative container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ease: "easeOut" }}
              className="max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-cyan-300 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <RocketLaunchIcon className="w-4 h-4" />
                <span>Live as of today</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
                Be among the first to{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  use JOE
                </span>
              </h2>
              <p className="text-xl mb-12 text-gray-300 max-w-2xl mx-auto leading-relaxed">
                Set up your profile in two minutes and watch your feed rebuild itself around the work you actually want.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <motion.div 
                  whileHover={{ scale: 1.05 }} 
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/register"
                    className="group px-12 py-6 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-2xl font-bold text-lg hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 flex items-center space-x-3"
                  >
                    <span>Build my feed — free</span>
                    <ArrowRightIcon className="w-6 h-6 transform group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
                <div className="text-sm text-gray-400">
                  ✓ Free to start  •  ✓ No credit card  •  ✓ Two-minute setup
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-300">
          <div className="container mx-auto px-4 py-12">
            {/* Main Footer Content */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-8">
              {/* Brand Section */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <BriefcaseIcon className="w-8 h-8 text-blue-400" />
                  <span className="text-2xl font-bold text-white">JOE</span>
                </div>
                <p className="text-gray-400">
                  Your AI career companion, from the first match to the signed offer.
                </p>
                <div className="flex space-x-4">
                  <a href="https://twitter.com/soffoalbert" className="hover:text-blue-400 transition-colors">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a href="https://www.linkedin.com/company/bros-joe-ai/about/?viewAsMember=true" className="hover:text-blue-400 transition-colors">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </a>
                </div>
              </div>
              
              {/* Quick Links */}
              <div>
                <h3 className="text-white font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li><Link to="/jobs" className="hover:text-blue-400 transition-colors">Browse Jobs</Link></li>
                  <li><Link to="/jobs" className="hover:text-blue-400 transition-colors">CV Analysis</Link></li>
                  <li><Link to="/pricing" className="hover:text-blue-400 transition-colors">Pricing</Link></li>
                  <li><Link to="/about" className="hover:text-blue-400 transition-colors">About Us</Link></li>
                </ul>
              </div>
              
              {/* Resources */}
              <div>
                <h3 className="text-white font-semibold mb-4">Resources</h3>
                <ul className="space-y-2">
                  <li><Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link></li>
                  <li><Link to="/guides" className="hover:text-blue-400 transition-colors">Career Guides</Link></li>
                  <li><Link to="/faq" className="hover:text-blue-400 transition-colors">FAQ</Link></li>
                  <li><Link to="/support" className="hover:text-blue-400 transition-colors">Support</Link></li>
                </ul>
              </div>
              
              {/* Contact */}
              <div>
                <h3 className="text-white font-semibold mb-4">Contact Us</h3>
                <div className="space-y-3">
                  <a href="mailto:soffoalbert@gmail.com" className="flex items-center space-x-2 hover:text-blue-400 transition-colors">
                    <EnvelopeIcon className="w-5 h-5" />
                    <span>soffoalbert@gmail.com</span>
                  </a>
                  <p className="text-sm text-gray-400">
                    Subscribe to our newsletter for the latest job search tips and updates.
                  </p>
                  <form className="flex space-x-2">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="flex-1 px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:border-blue-500"
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Subscribe
                    </button>
                  </form>
                </div>
              </div>
            </div>
            
            {/* Bottom Bar */}
            <div className="pt-8 mt-8 border-t border-gray-800 text-sm">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <div className="text-gray-400">
                  © {new Date().getFullYear()} JOE. All rights reserved.
                </div>
                <div className="flex space-x-6">
                  <Link to="/privacy" className="hover:text-blue-400 transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="hover:text-blue-400 transition-colors">Terms of Service</Link>
                  <Link to="/cookies" className="hover:text-blue-400 transition-colors">Cookie Policy</Link>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {isVideoOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsVideoOpen(false)}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-4 md:inset-10 z-50 flex flex-col bg-white rounded-3xl overflow-hidden shadow-2xl"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-xl font-semibold text-gray-900">Product Demo</h3>
                <button
                  onClick={() => setIsVideoOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              {/* Video Container */}
              <div className="flex-1 relative bg-black">
                <iframe
                  src="https://www.youtube.com/embed/YVyWpFlaKd4?autoplay=1"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, index }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, ease: "easeOut" }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative bg-white rounded-3xl p-8 shadow-sm hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 border border-gray-100/50 overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
      <div className="relative z-10">
        <div className="inline-flex p-4 bg-gradient-to-br from-blue-50 to-cyan-50 text-blue-600 rounded-2xl mb-6 group-hover:from-blue-500 group-hover:to-cyan-500 group-hover:text-white transition-all duration-300">
          {icon}
        </div>
        <h3 className="text-xl font-bold mb-4 text-gray-900 group-hover:text-gray-900">{title}</h3>
        <p className="text-gray-600 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
};

export default HomePage;